import type React from 'react';
import type * as TF from 'type-fest';
import Box from '#/ui/components/box';
import type { OverridableStringUnion, OverrideProps } from '#/ui/utils/types';
import { cn } from '#/utils/classnames';

export type ProgressPropsColorOverrides = TF.EmptyObject;

export interface ProgressOwnProps {
  /**
   * The current value of the progress indicator.
   * @default 0
   */
  value?: number;
  /**
   * The maximum value of the progress indicator.
   * @default 100
   */
  max?: number;
  /**
   * Override or extend the styles applied to the component.
   */
  className?: string;
  /**
   * The color of the progress bar.
   * @default 'primary'
   */
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error' | 'neutral',
    ProgressPropsColorOverrides
  >;
  /**
   * The size of the progress bar.
   * @default 'md'
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  // /**
  //  * If true, the progress will be indeterminate.
  //  * @default false
  //  */
  // indeterminate?: boolean;
}

export type ProgressTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'progress',
> = {
  props: AdditionalProps & ProgressOwnProps;
  defaultComponent: RootComponent;
};

export type ProgressProps<
  RootComponent extends React.ElementType = ProgressTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<ProgressTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

function Progress<C extends React.ElementType = 'progress'>(
  {
    component = 'progress',
    value = 0,
    max = 100,
    className,
    color = 'primary',
    size = 'md',
    // indeterminate = false,
    ...props
  }: ProgressProps<C>,
  ref?: React.ForwardedRef<C>
) {
  return (
    <Box
      {...props}
      component={component}
      ref={ref}
      value={value}
      max={max}
      // @todo indeterminate is not working
      // value={indeterminate ? undefined : value}
      // max={indeterminate ? undefined : max}
      className={cn(
        'gooey-progress',
        'd-progress',
        'w-full',
        colorMap[color],
        sizeMap[size],
        // indeterminate && 'd-progress-indeterminate',
        className
      )}
    />
  );
}
Progress.displayName = 'Progress';

export default Progress;

/*
|------------------
| Internals
|------------------
*/

const colorMap = {
  primary: 'd-progress-primary',
  secondary: 'd-progress-secondary',
  accent: 'd-progress-accent',
  info: 'd-progress-info',
  success: 'd-progress-success',
  warning: 'd-progress-warning',
  error: 'd-progress-error',
  neutral: 'd-progress-neutral',
} as const;

const sizeMap = {
  xs: 'h-1',
  sm: 'h-2',
  md: 'h-3',
  lg: 'h-4',
} as const;
